import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import { useI18n } from '../i18n/I18nContext';
import { CheckCircle, XCircle, Loader, RefreshCw } from 'lucide-react';
import Logo from '../components/Logo';
import '../styles/Auth.css';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { persistSession, resendVerification } = useAuth();
  const { t } = useI18n();

  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [resendEmail, setResendEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');

  const cooldownRef = useRef(null);
  const redirectRef = useRef(null);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      if (redirectRef.current) clearTimeout(redirectRef.current);
    };
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    authAPI.verifyEmail(token)
      .then((res) => {
        const payload = res?.data ?? res;
        if (payload?.access_token) {
          persistSession(payload);
        }
        setStatus('success');
        redirectRef.current = setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
      })
      .catch(() => {
        setStatus('error');
      });
  // Token is read once on mount — re-running on searchParams changes would
  // trigger a second verification attempt with the same token.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCooldown = () => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail || resendCooldown > 0 || resending) return;
    setResending(true);
    setResendError('');
    const result = await resendVerification(resendEmail);
    setResending(false);
    if (result.success) {
      startCooldown();
    } else {
      setResendError(result.error || 'Something went wrong.');
    }
  };

  return (
    <div className="page-shell">
      <div className="auth-container">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div className="auth-logo" style={{ justifyContent: 'center', marginBottom: 28 }}>
            <Logo height={40} />
          </div>

          {status === 'verifying' && (
            <>
              <div className="auth-verify-icon" style={{ margin: '0 auto 24px' }}>
                <Loader size={36} strokeWidth={1.5} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
              <h1 style={{ fontSize: '1.3rem' }}>{t('auth.verify.verifying')}</h1>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="auth-verify-icon" style={{ margin: '0 auto 24px', background: 'rgba(0, 219, 168, 0.12)', borderColor: 'rgba(0, 219, 168, 0.25)' }}>
                <CheckCircle size={36} strokeWidth={1.5} />
              </div>
              <h1 style={{ fontSize: '1.3rem' }}>{t('auth.verify.success')}</h1>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="auth-verify-icon" style={{ margin: '0 auto 24px', background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.2)', color: '#f87171' }}>
                <XCircle size={36} strokeWidth={1.5} />
              </div>
              <h1 style={{ fontSize: '1.3rem' }}>{t('auth.verify.invalidToken')}</h1>
              <p className="auth-subtitle" style={{ marginBottom: 24 }}>
                {t('auth.verify.resendPrompt')}
              </p>

              {resendError && <div className="error-message" style={{ marginBottom: 16 }}>{resendError}</div>}

              <form onSubmit={handleResend} style={{ textAlign: 'left' }}>
                <div className="form-group">
                  <label htmlFor="resend-email">{t('auth.fields.email')}</label>
                  <input
                    type="email"
                    id="resend-email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    placeholder={t('auth.fields.emailPlaceholder')}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary-auth"
                  disabled={resendCooldown > 0 || resending}
                >
                  <RefreshCw size={16} />
                  {resendCooldown > 0
                    ? t('auth.register.resendIn', { seconds: resendCooldown })
                    : t('auth.register.resendLink')}
                </button>
              </form>

              <p className="auth-footer" style={{ marginTop: 20 }}>
                <Link to="/login">{t('auth.register.backToLogin')}</Link>
              </p>
            </>
          )}
        </div>
      </div>

    </div>
  );
};

export default VerifyEmail;
